/**
 * Past-paper card — one per row in the library grid.
 *
 * Header layout:
 *   [status pill] ................... [preview] [reprocess if failed]
 *
 * The generate-questions action lives on the dedicated Generations
 * surface — it doesn't belong on the library card (it was noisy and
 * fired against a single paper only, which isn't how generation
 * should be scoped).
 *
 * Whole card is clickable → opens the detail drawer. The preview
 * button opens the PDF preview drawer (left-slide) — its click is
 * stopped so it doesn't also fire onOpen.
 */
import React from 'react';
import {
  AlertCircle, BookOpen, CalendarClock, CheckCircle2, ChevronRight,
  Eye, FileText, GraduationCap, Hash, Layers, Library, Loader2,
  RefreshCw,
} from 'lucide-react';
import { type Paper, getStatusMeta } from './types';

interface Props {
  paper: Paper;
  onOpen: () => void;
  onReprocess: () => void;
  onPreview: () => void;
}

export const PaperCard: React.FC<Props> = ({
  paper, onOpen, onReprocess, onPreview,
}) => {
  const meta = getStatusMeta(paper.status);
  const isReady   = paper.status === 'ready';
  const isFailed  = paper.status === 'failed';
  const isBusy    = !isReady && !isFailed;

  const stopAnd = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen(); }}
      className="
        group relative flex cursor-pointer flex-col gap-3
        overflow-hidden rounded-2xl border border-slate-200 bg-white p-4
        transition
        hover:-translate-y-0.5 hover:border-slate-300
        hover:shadow-[0_14px_32px_rgba(15,23,35,0.08)]
      "
    >
      {/* Tone accent — a soft top rail in the status colour so the
          library scans quickly by state. */}
      <span
        aria-hidden
        className={`absolute inset-x-0 top-0 h-[3px] ${meta.dotTw}`}
      />

      {/* Head: status + actions */}
      <div className="flex items-center justify-between">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-extrabold uppercase tracking-wider ${meta.tw}`}
        >
          {isFailed
            ? <AlertCircle size={11} />
            : isReady
              ? <CheckCircle2 size={11} />
              : <Loader2 size={11} className="animate-spin" />}
          {meta.label}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={stopAnd(onPreview)}
            title="Preview PDF"
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-700 transition hover:bg-[#1a5276]/10 hover:text-[#1a5276]"
          >
            <Eye size={13} />
          </button>
          {isFailed && (
            <button
              type="button"
              onClick={stopAnd(onReprocess)}
              title="Retry processing"
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#c0392b]/10 text-[#c0392b] transition hover:bg-[#c0392b]/20"
            >
              <RefreshCw size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex items-start gap-3">
        <span
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1a5276] to-[#0e9172] text-white shadow-[0_4px_12px_rgba(14,145,114,0.28)]"
        >
          <FileText size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <p
            className="m-0 truncate leading-none text-slate-900"
            style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20 }}
          >
            {paper.course_code || paper.course_title || 'Untitled'}
          </p>
          {paper.course_title && paper.course_code && (
            <p className="m-0 mt-0.5 truncate text-[12px] text-slate-600">
              {paper.course_title}
            </p>
          )}
          <p className="m-0 mt-1 truncate text-[11.5px] text-slate-400">
            {paper.file_name}
          </p>
        </div>
      </div>

      {/* Taxonomy path — Speciality › Level › Module › Unit.
          This is what the RAG index actually filters on, so it earns
          top billing above the year / chunks / generated chips. */}
      {(paper.speciality || paper.level || paper.module || paper.unit) && (
        <div className="rounded-lg bg-gradient-to-br from-[#1a5276]/6 to-[#0e9172]/6 px-2.5 py-2 ring-1 ring-[#1a5276]/10">
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-700">
            {paper.speciality && (
              <TaxChip icon={<GraduationCap size={10} />} tone="brand">
                {paper.speciality}
              </TaxChip>
            )}
            {paper.level && (
              <TaxChip icon={<Layers size={10} />} tone="accent">
                {paper.level}
              </TaxChip>
            )}
            {paper.module && (
              <TaxChip icon={<Library size={10} />} tone="gold">
                {paper.module}
              </TaxChip>
            )}
            {paper.unit && (
              <TaxChip icon={<BookOpen size={10} />} tone="purp">
                {paper.unit}
              </TaxChip>
            )}
          </div>
        </div>
      )}

      {/* Meta chips */}
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-600">
        {paper.year && (
          <Chip icon={<CalendarClock size={10} />}>{paper.year}</Chip>
        )}
        {paper.paper_type && <Chip>{paper.paper_type}</Chip>}
        {paper.chunk_count != null && (
          <Chip icon={<Layers size={10} />}>{paper.chunk_count} chunks</Chip>
        )}
        {paper.question_count != null && paper.question_count > 0 && (
          <Chip icon={<Hash size={10} />}>{paper.question_count} generated</Chip>
        )}
      </div>

      {/* Foot */}
      <div className="flex items-center justify-between border-t border-dashed border-slate-200 pt-2.5 text-[11.5px] text-slate-500">
        <span className="truncate">
          {isBusy && 'Ingestion in progress…'}
          {isReady  && (paper.embedding_model
            ? <>Embedded with <span className="font-semibold text-slate-700">{paper.embedding_model}</span></>
            : 'Ready to draw from')}
          {isFailed && (
            <span className="text-[#c0392b] truncate">
              {paper.error ? paper.error.slice(0, 60) : 'Ingestion failed'}
            </span>
          )}
        </span>
        <span className="inline-flex items-center gap-0.5 font-bold text-[#1a5276] transition group-hover:translate-x-0.5">
          Open <ChevronRight size={11} />
        </span>
      </div>
    </article>
  );
};

/* Small meta chip used in the middle row. */
const Chip: React.FC<{ icon?: React.ReactNode; children: React.ReactNode }> = ({
  icon, children,
}) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-semibold">
    {icon}
    <span>{children}</span>
  </span>
);

/* Tone-tinted chip for the taxonomy path — each level of the
   hierarchy gets its own accent so they visually cluster. */
const TONE: Record<'brand' | 'accent' | 'gold' | 'purp', { bg: string; fg: string }> = {
  brand:  { bg: 'bg-[#1a5276]/12', fg: 'text-[#1a5276]' },
  accent: { bg: 'bg-[#0e9172]/12', fg: 'text-[#0e9172]' },
  gold:   { bg: 'bg-[#b07d2b]/12', fg: 'text-[#b07d2b]' },
  purp:   { bg: 'bg-[#6d4aa6]/12', fg: 'text-[#6d4aa6]' },
};

const TaxChip: React.FC<{
  icon?: React.ReactNode;
  tone: 'brand' | 'accent' | 'gold' | 'purp';
  children: React.ReactNode;
}> = ({ icon, tone, children }) => {
  const t = TONE[tone];
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold ${t.bg} ${t.fg}`}
      title={String(children)}
    >
      {icon}
      <span className="truncate">{children}</span>
    </span>
  );
};
